// "Carichi di progetto": Fn / Fv / Fa, the α1 / α2 / Fv,Ed summary, service class and load duration (backend
// reference tables), the γM reference standard / column / value.
import { memo, useId, type FC } from "react";
import { FormField } from "../../../components/ds2/FormField";
import { Numeric } from "../../../components/ds2/Numeric";
import { Select } from "../../../components/ds2/Select";
import { Switch } from "../../../components/ds2/Switch";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import type { ServiceClassCode, Standard } from "../../../http/screwConnector/types/common";
import type { LoadDurationClassForUser, ServiceClassForUser } from "../../../http/screwConnector/types/normativeTables";
import { LOAD_DURATION_FROM_HTML } from "../../../http/screwConnector/formulas/data";
import type { GammaMColumn } from "../../../http/screwConnector/formulas/types";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import Html from "./Html";
import { useThreeLabels } from "./labels";
import { useMissing } from "./missingFields";
import { numOrNull, type EngineView } from "./uiState";
import { useNumberDraft } from "./useNumberDraft";
import { useScForm } from "./useScForm";
import styles from "./LoadsCard.module.css";

type LoadsCardProps = {
  view: EngineView;
  serviceClasses: ServiceClassForUser[];
  loadDurationClasses: LoadDurationClassForUser[];
};

const LoadsCard: FC<LoadsCardProps> = ({ view, serviceClasses, loadDurationClasses }) => {
  const disabled = useReadOnly();
  const t = useI18n().translation.screwConnectorModule.calculation.loadsSection;
  const switchId = useId();
  const { values, set } = useScForm();
  const draft = useNumberDraft();
  const isMissing = useMissing();
  const { ui } = view;
  const ev = view.values;
  const connectionType: ConnectionType = ev?.materiale1 ?? values.connectionType;
  const labels = useThreeLabels(connectionType);

  const serviceClassOptions = serviceClasses
    .slice()
    .sort((a, b) => a.code - b.code)
    .map((sc) => ({ value: String(sc.code), label: `${sc.code} — ${sc.name}` }));
  const loadDurationOptions = loadDurationClasses
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((ld) => ({ value: ld.code, label: ld.name }));
  const standardOptions = (["EC5", "NTC2018"] as const).map((value) => ({ value, label: t.standards[value] }));
  const columnOptions = (["A", "B"] as const).map((value) => ({ value, label: t.columns[value] }));
  // the engine works with the HTML keys (#durataCarico); the select with the backend codes
  const loadDuration = values.loadDurationClassCode ?? (ev ? (LOAD_DURATION_FROM_HTML[ev.durataCarico] ?? null) : null);
  const serviceClass = ev ? String(ev.classeServizio) : values.serviceClassCode == null ? "" : String(values.serviceClassCode);
  const standard = ev ? ev.normativaGammaM : (values.normativeStandard ?? "EC5");
  const column = ev ? ev.gammaMColonna : (values.gammaMColumn ?? "A");
  const gammaM = ev ? numOrNull(ev.gammaM) : values.gammaMValue;

  const force = (name: "forceFn" | "forceFv" | "forceFa", label: string, unit: string) => {
    const value = ev ? numOrNull(ev[name]) : values[name];
    return (
      <FormField label={label} footer={{ type: "info", message: unit }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <Numeric
            id={labelId}
            name={name}
            allowNegative
            disabled={disabled}
            isInvalid={isMissing(name, value === null)}
            {...draft(name, value, (v) => set(name, v))}
          />
        )}
      </FormField>
    );
  };

  return (
    <>
      <div className={styles.row}>
        {force("forceFn", t.fn, t.fnUnit)}
        {force("forceFv", t.fv, t.fvUnit)}
        {force("forceFa", t.fa, t.faUnit)}
      </div>
      <p className={styles.summary}>
        α<sub>1</sub> = atan2(Fv, Fn) = <strong>{ui.labels.alpha1Display}</strong> ({t.resultantAngle}{" "}
        {labels.alpha1Element}) · α<sub>2</sub> = θ − α<sub>1</sub> = <strong>{ui.labels.alpha2Display}</strong> (
        {t.resultantAngle} {labels.alpha2Element}) · F<sub>v,Ed</sub> = √(Fn²+Fv²) ={" "}
        <strong>{ui.labels.edDisplay}</strong> kN · Fa ({t.withdrawal}) = <strong>{ui.labels.faDisplay}</strong> kN —{" "}
        {t.notYetVerified}
      </p>
      <FormField label={t.serviceClass} footer={{ type: "info", message: "EC5 §2.3.1.3" }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <Select
            inputId={labelId}
            name="classeServizio"
            options={serviceClassOptions}
            value={serviceClassOptions.find((o) => o.value === serviceClass) ?? null}
            isDisabled={disabled}
            isInvalid={isMissing("classeServizio", !serviceClass)}
            isSearchable={false}
            placeholder=""
            onChange={(o) => o && o.value !== serviceClass && set("serviceClassCode", Number(o.value) as ServiceClassCode)}
          />
        )}
      </FormField>
      <FormField label={t.loadDuration} footer={{ type: "info", message: "EC5 Tab.3.1 / NTC2018" }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <Select
            inputId={labelId}
            name="durataCarico"
            options={loadDurationOptions}
            value={loadDurationOptions.find((o) => o.value === loadDuration) ?? null}
            isDisabled={disabled}
            isInvalid={isMissing("durataCarico", !loadDuration)}
            isSearchable={false}
            placeholder=""
            onChange={(o) => o && o.value !== loadDuration && set("loadDurationClassCode", o.value)}
          />
        )}
      </FormField>
      <div className={styles.row}>
        <FormField label={t.gammaMRef} footer={{ type: "info", message: t.gammaMRefUnit }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Select
              inputId={labelId}
              name="normativaGammaM"
              options={standardOptions}
              value={standardOptions.find((o) => o.value === standard) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing("normativaGammaM", !standard)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== standard && set("normativeStandard", o.value as Standard)}
            />
          )}
        </FormField>
        {ui.visible.gammaMColonnaWrap ? (
          <FormField label={t.column} footer={{ type: "info", message: "NTC2018" }} disabled={disabled}>
            {({ labelId, disabled }) => (
              <Select
                inputId={labelId}
                name="gammaMColonna"
                options={columnOptions}
                value={columnOptions.find((o) => o.value === column) ?? null}
                isDisabled={disabled}
                isInvalid={isMissing("gammaMColonna", !column)}
                isSearchable={false}
                placeholder=""
                onChange={(o) => o && o.value !== column && set("gammaMColumn", o.value as GammaMColumn)}
              />
            )}
          </FormField>
        ) : null}
      </div>
      <FormField label="γM" disabled={disabled || ui.disabled.gammaM}>
        {({ labelId, disabled }) => (
          <Numeric
            id={labelId}
            name="gammaM"
            decimalScale={2}
            disabled={disabled}
            isInvalid={isMissing("gammaM", gammaM === null)}
            {...draft("gammaM", gammaM, (v) => set("gammaMValue", v))}
          />
        )}
      </FormField>
      <Switch
        id={`${switchId}-gammaMAuto`}
        checked={ev ? ev.gammaMAuto : values.gammaMAuto}
        disabled={disabled}
        onChange={(v) => set("gammaMAuto", v)}
      >
        <Html text={t.gammaMAuto} />
      </Switch>
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(LoadsCard);
